import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, map, tap, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DevLoggerService } from '../shared/services/dev-logger.service';

// All domain models live in calculation-state.models.ts.
// Re-exported here so existing imports from this path keep compiling unchanged.
export * from './calculation-state.models';
import {
  CalculationOperation, CalculationResult, CompleteCalculationRequest, FailCalculationRequest,
  PbReservation, PendingPbSelection, PlanningCollision, QueuedActionStatus, QueuedActionType,
  ReservePbsRequest, StartCalculationRequest, StartCalculationResponse
} from './calculation-state.models';

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════════

@Injectable({ providedIn: 'root' })
export class CalculationStateService {

  private baseUrl = environment.tmsApiEndpointAddress + 'calculationstate';

  // Local state for the dashboard panel
  private _operations = new BehaviorSubject<CalculationOperation[]>([]);
  public operations$ = this._operations.asObservable();

  // Cross-tab sync: when the user has multiple tabs open (e.g. PB-Screen + Planning Board)
  // we broadcast the operations snapshot so every tab can show the same shimmer / status.
  // Guarded by `_isApplyingBroadcast` to prevent re-broadcast loops.
  private _bc: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('pt4-optimistic-calc') : null;
  private _isApplyingBroadcast = false;

  /**
   * Update the operations BehaviorSubject AND broadcast the snapshot to other tabs.
   * Use this instead of calling `_operations.next(...)` directly so cross-tab sync
   * stays consistent. Does not broadcast when the update originated from another tab.
   */
  private _setOperations(ops: CalculationOperation[]): void {
    this._operations.next(ops);
    if (this._bc && !this._isApplyingBroadcast) {
      try {
        this._bc.postMessage({ type: 'ops-snapshot', operations: ops });
      } catch {
        // BroadcastChannel can throw on disposed/closed; non-fatal.
      }
    }
  }

  /** Snapshot of current operations (synchronous read from BehaviorSubject) */
  get operations(): CalculationOperation[] {
    return this._operations.value;
  }

  /** Set of PB IDs currently in a 'Calculating' operation — for freezing UI elements */
  public calculatingPbIds$ = this.operations$.pipe(
    map(ops => {
      const ids = new Set<string>();
      for (const op of ops) {
        if (op.status === 'Calculating') {
          op.planningBlockIds?.forEach(id => ids.add(String(id)));
        }
      }
      return ids;
    }),
    distinctUntilChanged((a, b) => a.size === b.size && [...a].every(id => b.has(id)))
  );

  /** Synchronous snapshot of PB IDs currently being calculated */
  getCalculatingPbIds(): Set<string> {
    const ids = new Set<string>();
    for (const op of this._operations.value) {
      if (op.status === 'Calculating') {
        op.planningBlockIds?.forEach(id => ids.add(String(id)));
      }
    }
    return ids;
  }

  /** Emits a truckName/driverName (and optional operationId) when the activity panel wants to pin a specific Slot Dot */
  private _pinSlot = new Subject<{ truckName: string; driverName: string; operationId?: string }>();
  public pinSlot$ = this._pinSlot.asObservable();

  pinSlot(truckName: string, driverName: string, operationId?: string): void {
    this._pinSlot.next({ truckName, driverName, operationId });
  }

  /** Tracks the currently pinned slot (null = nothing pinned). Used to enforce single-pin and block hover. */
  private _pinnedSlot = new BehaviorSubject<{ truckName: string; driverName: string } | null>(null);
  public pinnedSlot$ = this._pinnedSlot.asObservable();

  get pinnedSlot() { return this._pinnedSlot.value; }

  setPinnedSlot(truckName: string, driverName: string): void {
    this._pinnedSlot.next({ truckName, driverName });
  }

  clearPinnedSlot(): void {
    this._pinnedSlot.next(null);
  }

  // ── Pending PBs (selected, waiting to be planned) ──

  /** PBs currently selected by this user, waiting to be planned (from PB screen — with sequence) */
  private _pendingPbs = new BehaviorSubject<PendingPbSelection | null>(null);
  public pendingPbs$ = this._pendingPbs.asObservable();

  private _pendingPbsSetAt = 0;

  setPendingPbs(selection: PendingPbSelection | null): void {
    if (selection !== null) {
      this._pendingPbsSetAt = Date.now();
    } else if (Date.now() - this._pendingPbsSetAt < 100) {
      return; // debounce: ignore rapid clear-then-set on selection change
    }
    this._pendingPbs.next(selection);
  }

  get pendingPbsSnapshot(): PendingPbSelection | null {
    return this._pendingPbs.value;
  }

  /** All active reservations from all users (polled from backend every 10s) */
  private _allReservations = new BehaviorSubject<PbReservation[]>([]);
  public allReservations$ = this._allReservations.asObservable();

  private _reservationPollSub: any = null;

  startReservationPolling(): void {
    if (this._reservationPollSub) return;
    this._pollReservations();
    this._reservationPollSub = setInterval(() => this._pollReservations(), 10_000);
  }

  stopReservationPolling(): void {
    if (this._reservationPollSub) {
      clearInterval(this._reservationPollSub);
      this._reservationPollSub = null;
    }
  }

  private _pollReservations(): void {
    this.getReservedPbs().subscribe(reservations => {
      this._allReservations.next(reservations);
    });
  }

  constructor(
    private http: HttpClient,
    private devLogger: DevLoggerService,
    private zone: NgZone,
  ) {
    // Listen for snapshots from other tabs and apply them locally without re-broadcasting.
    // Note: BroadcastChannel events fire OUTSIDE Angular's zone (zone.js doesn't patch
    // it), so subscribers wouldn't trigger change detection. Wrap in zone.run() so
    // the PB-Screen grid + activity panel actually re-render the shimmer.
    if (this._bc) {
      this._bc.onmessage = (event) => {
        if (event?.data?.type === 'ops-snapshot' && Array.isArray(event.data.operations)) {
          this.zone.run(() => {
            this._isApplyingBroadcast = true;
            try {
              // Reconcile instead of blind-replace: a snapshot from another tab must never
              // downgrade or drop an op that just completed locally (which would erase its chip).
              this._operations.next(this._reconcileOps(event.data.operations as CalculationOperation[]));
            } finally {
              this._isApplyingBroadcast = false;
            }
          });
        }
      };
    }
  }

  // ── PB Reservation API ──

  reservePbs(request: ReservePbsRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/reserve-pbs`, request).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Reserve PBs failed (non-blocking):', err?.status);
        return [null];
      })
    );
  }

  releasePbs(userId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/reserve-pbs?userId=${encodeURIComponent(userId)}`).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Release PBs failed (non-blocking):', err?.status);
        return [null];
      })
    );
  }

  getReservedPbs(): Observable<PbReservation[]> {
    return this.http.get<PbReservation[]>(`${this.baseUrl}/reserved-pbs`).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Get reserved PBs failed:', err?.status);
        return [[]];
      })
    );
  }

  // ── API Methods ──

  startCalculation(request: StartCalculationRequest): Observable<StartCalculationResponse> {
    // ── Client-side collision check (safety net if server is unreachable) ──
    const clientCollisions = this._detectLocalCollisions(request);
    if (clientCollisions.length > 0) {
      const localOpId = 'local-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
      const op: CalculationOperation = {
        operationId: localOpId,
        localOpId: localOpId,
        userId: request.userId,
        userName: request.userName,
        operationType: request.operationType,
        planningBlockIds: request.planningBlockIds,
        planningBlockDetails: request.planningBlockDetails,
        truckName: request.truckName,
        driverName: request.driverName,
        startedAt: new Date().toISOString(),
        status: 'Calculating',
      };
      this._setOperations([op, ...this._operations.value]);
      this._applyCollisionFailure(localOpId, clientCollisions);
      this.devLogger.warn('CALC-STATE', 'Client-side collision detected:', clientCollisions.map(c => c.type));
      return of({ accepted: false, operationId: localOpId, collisions: clientCollisions } as StartCalculationResponse);
    }

    // Generate a local operation ID and add to local state IMMEDIATELY
    // so the dot appears even if the API is unreachable.
    const localOpId = 'local-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
    const op: CalculationOperation = {
      operationId: localOpId,
      localOpId: localOpId,
      userId: request.userId,
      userName: request.userName,
      operationType: request.operationType,
      planningBlockIds: request.planningBlockIds,
      planningBlockDetails: request.planningBlockDetails,
      truckId: request.truckId,
      truckName: request.truckName,
      driverName: request.driverName,
      sourceTruckName: request.sourceTruckName,
      sourceDriverName: request.sourceDriverName,
      planDate: request.planDate,
      uiPlanDate: request.uiPlanDate,
      startedAt: new Date().toISOString(),
      status: 'Calculating',
      queuedAction: request.queuedAction || null,
      queuedActionStatus: request.queuedAction ? 'pending' : null
    };
    this._setOperations([op, ...this._operations.value]);
    this.devLogger.log('CALC-STATE', 'Started (local):', localOpId, '| Type:', request.operationType, '| PBs:', request.planningBlockIds, '| Truck:', request.truckName);

    // Strip UI-only fields before sending to backend.
    const { uiPlanDate: _uiPlanDate, ...backendRequest } = request;
    return this.http.post<StartCalculationResponse>(`${this.baseUrl}/start`, backendRequest).pipe(
      tap(res => {
        if (res.accepted && res.operationId) {
          // Replace local ID with server ID
          const ops = this._operations.value.map(o =>
            o.operationId === localOpId ? { ...o, operationId: res.operationId } : o
          );
          this._setOperations(ops);
          this.devLogger.log('CALC-STATE', 'Server accepted:', res.operationId, '(was', localOpId, ')');
        } else if (!res.accepted) {
          if (res.collisions?.length) {
            this._applyCollisionFailure(localOpId, res.collisions);
          } else {
            this.devLogger.warn('CALC-STATE', 'Rejected - conflicts:', res.conflicts);
            this._setOperations(this._operations.value.filter(o => o.operationId !== localOpId));
          }
        }
      }),
      map(res => ({ ...res, operationId: res.operationId || localOpId })),
      catchError(err => {
        if (err?.status === 409 && err?.error?.collisions?.length) {
          const collisions: PlanningCollision[] = err.error.collisions;
          this._applyCollisionFailure(localOpId, collisions);
          const rejected: StartCalculationResponse = { accepted: false, operationId: localOpId, collisions };
          return [rejected];
        }
        this.devLogger.warn('CALC-STATE', 'Start API call failed (keeping local state):', err?.status, err?.message);
        return [{ accepted: true, operationId: localOpId } as StartCalculationResponse];
      })
    );
  }

  /** Client-side collision detection — catches conflicts even if the server is unreachable */
  private _detectLocalCollisions(request: StartCalculationRequest): PlanningCollision[] {
    const collisions: PlanningCollision[] = [];
    const requestedPbIds = new Set(request.planningBlockIds.map(id => String(id)));

    for (const op of this._operations.value) {
      if (op.status !== 'Calculating') continue;
      // The user's OWN in-flight operations collide too: a second plan/unplan touching the same
      // PBs or slot fires a concurrent BC write chain against the same Transport Order, which BC
      // rejects with its stale-record error ("...not up-to-date"). Cross-tab included — the
      // operations list is mirrored between the planner's tabs.
      const isOwnOperation = op.userId === request.userId;

      // Check PB overlap
      const conflicting = (op.planningBlockIds || []).filter(id => requestedPbIds.has(String(id)));
      if (conflicting.length > 0) {
        collisions.push({
          type: 'PbInCalculation' as any,
          blockingUserId: op.userId,
          blockingUserName: op.userName,
          operationType: op.operationType,
          truckName: op.truckName,
          since: op.startedAt,
          conflictingPbIds: conflicting,
          userFriendlyMessage: isOwnOperation
            ? `Your previous ${op.operationType === 'unplanPb' ? 'unplan' : 'plan'} action on these planning blocks is still running. Please wait for it to complete.`
            : `These planning blocks are currently being processed by ${op.userName || op.userId}. Please wait for their operation to complete.`
        });
      }

      // Check slot overlap (same truck/driver)
      if (request.truckName && op.truckName && request.truckName === op.truckName) {
        if (!collisions.some(c => c.type === ('SlotBusy' as any))) {
          collisions.push({
            type: 'SlotBusy' as any,
            blockingUserId: op.userId,
            blockingUserName: op.userName,
            operationType: op.operationType,
            truckName: op.truckName,
            since: op.startedAt,
            conflictingPbIds: [],
            userFriendlyMessage: isOwnOperation
              ? `Your previous action on this truck/driver slot is still running. Please wait for it to complete.`
              : `This truck/driver slot is currently being used by ${op.userName || op.userId}. Please wait or choose a different slot.`
          });
        }
      }
    }
    return collisions;
  }

  completeCalculation(operationId: string, result: CalculationResult): Observable<any> {
    // Update local state FIRST (resilient — works even if API is down).
    // Completed ops expire after 10 minutes (matches backend CompletedTtl).
    this._updateLocalOperation(operationId, {
      status: 'Completed' as const,
      completedAt: new Date().toISOString(),
      durationMs: this._calcDuration(operationId),
      expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      result
    });
    // Resolve the server operationId (may differ from the local ID passed in)
    const serverOpId = this._resolveServerOpId(operationId);
    this.devLogger.log('CALC-STATE', 'Completed (local):', operationId,
      '| TO:', result.createdTransportOrderId, '| PB outcomes:', result.planningBlockOutcomes?.length,
      '| Conflicts:', result.conflicts?.length, '| Warnings:', result.warnings?.length);

    // Don't call server API for local-only operations (server may not track PB actions)
    if (serverOpId.startsWith('local-')) return of(null);

    return this.http.post(`${this.baseUrl}/complete`, { operationId: serverOpId, result } as CompleteCalculationRequest).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Complete API call failed (local state already updated):', err?.status);
        return [null];
      })
    );
  }

  failCalculation(operationId: string, errorMessage: string): Observable<any> {
    // Update local state FIRST (resilient).
    // Failed ops expire after 30 minutes (matches backend FailedTtl).
    this._updateLocalOperation(operationId, {
      status: 'Failed' as const,
      completedAt: new Date().toISOString(),
      durationMs: this._calcDuration(operationId),
      expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      result: { success: false, errorMessage }
    });
    // Resolve the server operationId (may differ from the local ID passed in)
    const serverOpId = this._resolveServerOpId(operationId);
    this.devLogger.log('CALC-STATE', 'Failed (local):', operationId, '| Error:', errorMessage);

    // Don't call server API for local-only operations
    if (serverOpId.startsWith('local-')) return of(null);

    return this.http.post(`${this.baseUrl}/fail`, { operationId: serverOpId, errorMessage } as FailCalculationRequest).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Fail API call failed (local state already updated):', err?.status);
        return [null];
      })
    );
  }

  /** Refresh operations from server (for page load / periodic refresh) */
  refreshOperations(userId?: string): Observable<CalculationOperation[]> {
    const params = userId ? `?userId=${userId}` : '';
    return this.http.get<CalculationOperation[]>(`${this.baseUrl}/operations${params}`).pipe(
      tap(ops => {
        // Normalize status: API may return numeric enum (0,1,2) instead of string
        const STATUS_MAP: Record<number, string> = { 0: 'Calculating', 1: 'Completed', 2: 'Failed' };
        ops.forEach(op => {
          if (typeof op.status === 'number') {
            (op as any).status = STATUS_MAP[op.status as any] || op.status;
          }
        });
        // Reconcile so a server snapshot that hasn't caught up (e.g. a board-tab refresh
        // firing right after a depot op completed locally) can't wipe the local result.
        this._setOperations(this._reconcileOps(ops));
        const active = ops.filter(o => o.status === 'Calculating').length;
        const completed = ops.filter(o => o.status === 'Completed').length;
        const failed = ops.filter(o => o.status === 'Failed').length;
        this.devLogger.log('CALC-STATE', 'Refreshed from server:', ops.length, 'ops | Active:', active, '| Completed:', completed, '| Failed:', failed);
      }),
      catchError(err => {
        this.devLogger.error('CALC-STATE', 'Refresh failed:', err?.status, err?.message);
        return [];
      })
    );
  }

  dismissOperation(operationId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${operationId}`).pipe(
      tap(() => {
        this._setOperations(this._operations.value.filter(op => op.operationId !== operationId));
      })
    );
  }

  /**
   * Remove a single operation from local state immediately (and broadcast to other tabs),
   * with a best-effort server delete for non-local ops. Used by the depot inline indicator
   * where the user dismisses a finished (Completed/Failed) result chip. Unlike
   * `dismissOperation`, this never waits on the server, so the chip disappears instantly
   * even when the backend is unreachable.
   */
  dismissOperationLocal(operationId: string): void {
    const op = this._operations.value.find(o => o.operationId === operationId || o.localOpId === operationId);
    this._setOperations(this._operations.value.filter(o => o.operationId !== operationId && o.localOpId !== operationId));
    const serverOpId = op?.operationId;
    if (serverOpId && !serverOpId.startsWith('local-')) {
      this.http.delete(`${this.baseUrl}/${serverOpId}`).pipe(
        catchError(() => [null])
      ).subscribe();
    }
  }

  clearCompleted(userId?: string): Observable<any> {
    const params = userId ? `?userId=${userId}` : '';
    return this.http.delete(`${this.baseUrl}/completed${params}`).pipe(
      tap(() => {
        this._setOperations(this._operations.value.filter(op => op.status !== 'Completed'));
      })
    );
  }

  dismissAllFinished(): void {
    const ops = this._operations.value;
    const toRemove = ops.filter(op => op.status === 'Completed' || op.status === 'Failed');
    if (!toRemove.length) return;
    this._setOperations(ops.filter(op => op.status === 'Calculating'));
    for (const op of toRemove) {
      if (!op.operationId.startsWith('local-')) {
        this.http.delete(`${this.baseUrl}/${op.operationId}`).subscribe();
      }
    }
  }

  // ── Safety: auto-fail stuck Calculating operations ──

  /** Max time a Calculating operation can stay active before being auto-failed.
   *  Matches the server-side CalculatingTtl (60s) plus a small buffer. */
  static readonly CALCULATING_TIMEOUT_MS = 90_000;

  /** Collision-caused failures auto-clear faster than real failures (2 min vs 30 min). */
  static readonly COLLISION_EXPIRY_MS = 2 * 60 * 1000;

  /**
   * Call from any 1-second tick interval. Auto-fails operations that have been
   * stuck in Calculating status longer than CALCULATING_TIMEOUT_MS. Prevents
   * permanent UI lockout if the client crashes mid-operation or the BC call hangs.
   */
  expireStuckOperations(): void {
    const now = Date.now();
    let changed = false;
    const ops = this._operations.value.map(op => {
      if (op.status !== 'Calculating') return op;
      const elapsed = now - new Date(op.startedAt).getTime();
      if (elapsed < CalculationStateService.CALCULATING_TIMEOUT_MS) return op;

      this.devLogger.warn('CALC-STATE', 'Auto-failing stuck operation:', op.operationId, '| Elapsed:', Math.round(elapsed / 1000) + 's');
      changed = true;
      return {
        ...op,
        status: 'Failed' as const,
        completedAt: new Date().toISOString(),
        durationMs: elapsed,
        expiresAt: new Date(now + 5 * 60 * 1000).toISOString(),
        result: { success: false, errorMessage: 'Operation timed out — the server did not respond within the expected time. The row and planning blocks are now available again. You can retry the action.' }
      };
    });
    if (changed) this._setOperations(ops);
  }

  // ── Local state helpers (resilient — work without API) ──

  private _applyCollisionFailure(localOpId: string, collisions: PlanningCollision[]): void {
    const collisionMessage = collisions.map(c => c.userFriendlyMessage).join(' ');
    this._updateLocalOperation(localOpId, {
      status: 'Failed',
      completedAt: new Date().toISOString(),
      durationMs: 0,
      expiresAt: new Date(Date.now() + CalculationStateService.COLLISION_EXPIRY_MS).toISOString(),
      result: { success: false, errorMessage: collisionMessage },
      collisions
    });
    this.devLogger.warn('CALC-STATE', 'Collision:', collisions.map(c => c.type).join(', '), '| Message:', collisionMessage);
  }

  /** Resolve a possibly-local operationId to the server-assigned operationId */
  private _resolveServerOpId(operationId: string): string {
    const op = this._operations.value.find(o => o.operationId === operationId || o.localOpId === operationId);
    return op?.operationId || operationId;
  }

  private _updateLocalOperation(operationId: string, patch: Partial<CalculationOperation>): void {
    let matched = false;
    const ops = this._operations.value.map(op => {
      if (op.operationId === operationId || op.localOpId === operationId) {
        matched = true;
        return { ...op, ...patch };
      }
      return op;
    });
    if (!matched) {
      // Diagnostic: a status update (e.g. completeCalculation) landed on an op that is no
      // longer in the list. The inline chip will be missing because there's nothing to mark
      // Completed. Depot callers self-heal via upsertTerminalOperation; this log pinpoints it.
      this.devLogger.warn('CALC-STATE', 'Update for missing operation (no-op):', operationId, '| status:', (patch as any).status);
    }
    this._setOperations(ops);
  }

  /** Status precedence for snapshot reconciliation: terminal beats Calculating beats unknown. */
  private _statusRank(status: any): number {
    return status === 'Completed' || status === 'Failed' ? 2 : status === 'Calculating' ? 1 : 0;
  }

  /**
   * Reconcile an incoming operations snapshot (server refresh or another tab's broadcast)
   * against current local state WITHOUT losing newer local progress:
   *  - never downgrade a local op (e.g. Completed) to an earlier incoming status, and
   *  - keep local active/terminal ops the snapshot doesn't yet know about (e.g. an op that
   *    just completed locally before the server learned of it).
   * Prevents a stale full-replace from erasing a success/fail chip.
   */
  private _reconcileOps(incoming: CalculationOperation[]): CalculationOperation[] {
    const local = this._operations.value;
    if (!incoming?.length) return local;
    const sameOp = (a: CalculationOperation, b: CalculationOperation) =>
      a.operationId === b.operationId ||
      (!!a.localOpId && (a.localOpId === b.localOpId || a.localOpId === b.operationId)) ||
      (!!b.localOpId && b.localOpId === a.operationId);
    const merged = incoming.map(inc => {
      const loc = local.find(l => sameOp(l, inc));
      return loc && this._statusRank(loc.status) > this._statusRank(inc.status) ? loc : inc;
    });
    for (const loc of local) {
      if (this._statusRank(loc.status) >= 1 && !incoming.some(inc => sameOp(loc, inc))) {
        merged.unshift(loc); // keep newest-first
      }
    }
    return merged;
  }

  /**
   * Re-insert a terminal (Completed/Failed) operation that went missing from the list, so an
   * inline result chip is never silently lost. No-op when the operation is already present.
   * Used by the depot optimistic flow as a self-heal after completeCalculation.
   */
  upsertTerminalOperation(op: CalculationOperation): void {
    const exists = this._operations.value.some(o =>
      o.operationId === op.operationId || (!!op.localOpId && o.localOpId === op.localOpId));
    if (exists) return;
    this.devLogger.warn('CALC-STATE', 'Re-inserting missing terminal operation:', op.operationId, '| status:', op.status);
    this._setOperations([op, ...this._operations.value]);
  }

  private _calcDuration(operationId: string): number {
    const op = this._operations.value.find(o => o.operationId === operationId || o.localOpId === operationId);
    if (!op) return 0;
    return Date.now() - new Date(op.startedAt).getTime();
  }

  /** Set or clear a queued post-calculation action */
  setQueuedAction(operationId: string, action: QueuedActionType | null): void {
    const status = action ? 'pending' : null;
    this._updateLocalOperation(operationId, {
      queuedAction: action,
      queuedActionStatus: status,
      queuedActionError: null
    });
    this.devLogger.log('CALC-STATE', 'Queued action:', action || '(cleared)', '| Op:', operationId);
    this._syncQueuedActionToApi(operationId, action, status);
  }

  /** Update queued action execution status */
  updateQueuedActionStatus(operationId: string, status: QueuedActionStatus, error?: string): void {
    this._updateLocalOperation(operationId, {
      queuedActionStatus: status,
      queuedActionError: error || null
    });
    const op = this._operations.value.find(o => o.operationId === operationId);
    this._syncQueuedActionToApi(operationId, op?.queuedAction || null, status, error);
  }

  private _syncQueuedActionToApi(operationId: string, action: QueuedActionType | null, status: QueuedActionStatus | string | null, error?: string): void {
    if (operationId.startsWith('local-')) return;
    this.http.post(`${this.baseUrl}/queued-action`, {
      operationId,
      queuedAction: action,
      queuedActionStatus: status,
      queuedActionError: error || null
    }).pipe(
      catchError(err => {
        this.devLogger.warn('CALC-STATE', 'Failed to sync queued action to API:', err?.status);
        return [null];
      })
    ).subscribe();
  }

  /** Mark an operation as verified (user acknowledged the result) */
  verifyOperation(operationId: string): void {
    const ops = this._operations.value.map(op =>
      op.operationId === operationId ? { ...op, verified: true } : op
    );
    this._setOperations(ops);
  }

  /** Get operations filtered for a specific capacity slot (by truck or driver, including source slot) */
  getOperationsForSlot(truckId?: string, truckName?: string, driverName?: string): CalculationOperation[] {
    return this._operations.value.filter(op =>
      (truckId && op.truckId && op.truckId === truckId) ||
      (truckName && op.truckName && op.truckName === truckName) ||
      (driverName && op.driverName && op.driverName === driverName) ||
      (truckName && op.sourceTruckName && op.sourceTruckName === truckName) ||
      (driverName && op.sourceDriverName && op.sourceDriverName === driverName)
    );
  }

  // ── Computed getters ──

  get activeCount(): number {
    return this._operations.value.filter(op => op.status === 'Calculating').length;
  }

  /** Synchronous snapshot of all operations — use for guards, not for reactive UI */
  getOperationsSnapshot(): CalculationOperation[] {
    return this._operations.value;
  }

  get hasErrors(): boolean {
    return this._operations.value.some(op => op.status === 'Failed');
  }

  get hasNewResults(): boolean {
    return this._operations.value.some(op => op.status === 'Completed');
  }
}
